import React, { useState, useEffect } from "react";
import { 
  AlertTriangle, 
  Ban, 
  Clock, 
  Shield, 
  Users, 
  Gavel,
  RefreshCw,
  UserCheck,
  TrendingUp,
  Calendar,
  Search
} from "lucide-react";
import Swal from "sweetalert2";
import { moderationService } from "../../services/moderationService";
import UserSuspensionBadge from "./UserSuspensionBadge";

export default function ModerationPanel() {
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [allUsers, searchTerm, statusFilter, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsData, allUsersData] = await Promise.all([
        moderationService.getStats(),
        moderationService.getAllUsers()
      ]);
      setStats(statsData);
      setAllUsers(allUsersData);
    } catch (error) {
      console.error("Error al cargar datos:", error);
      Swal.fire("Error!", "No se pudieron cargar los datos de moderación", "error");
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...allUsers];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.name.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        user.cpf?.includes(term) ||
        user.phone?.includes(term)
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(user => {
        if (statusFilter === "warning") return user.warning_count > 0 && !user.is_permanently_banned && !user.suspended_until;
        if (statusFilter === "suspended") return user.suspended_until && new Date(user.suspended_until) > new Date();
        if (statusFilter === "banned") return user.is_permanently_banned === 1;
        return true;
      });
    }

    if (activeTab === 'sanctioned') {
      filtered = filtered.filter(user => 
        user.warning_count > 0 || 
        (user.suspended_until && new Date(user.suspended_until) > new Date()) ||
        user.is_permanently_banned === 1
      );
    }

    setFilteredUsers(filtered);
  };

  const showWarningModal = async (user) => {
    const { value: formData } = await Swal.fire({
      title: 'Aplicar Advertência',
      html: `
        <div class="text-left">
          <p class="mb-2 text-sm text-gray-600">Usuário: <strong>${user.name}</strong></p>
          <p class="mb-2 text-xs text-gray-500">Advertências atuais: ${user.warning_count || 0}</p>
          <textarea id="reason" class="swal2-textarea" placeholder="Motivo da advertência..." rows="3"></textarea>
          <textarea id="description" class="swal2-textarea mt-3" placeholder="Descrição detalhada (opcional)..." rows="2"></textarea>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: "#eab308",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Aplicar Advertência",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const reason = document.getElementById('reason').value;
        if (!reason) {
          Swal.showValidationMessage('Informe o motivo da advertência');
          return false;
        }
        return {
          reason: reason,
          description: document.getElementById('description').value
        };
      }
    });

    if (formData) {
      try {
        const result = await moderationService.warnUser(user.id, formData.reason, formData.description);
        Swal.fire({
          icon: 'success',
          title: 'Advertência aplicada!',
          html: result.message,
          timer: 3000
        });
        fetchData();
      } catch (error) {
        Swal.fire('Erro!', 'Não foi possível aplicar a advertência', 'error');
      }
    }
  };

  const showSuspensionModal = async (user) => {
    const { value: formData } = await Swal.fire({
      title: 'Suspender Usuário',
      html: `
        <div class="text-left">
          <p class="mb-2 text-sm text-gray-600">Usuário: <strong>${user.name}</strong></p>
          <select id="days" class="swal2-select mb-3">
            <option value="1">1 dia</option>
            <option value="3">3 dias</option>
            <option value="7">7 dias</option>
            <option value="14">14 dias</option>
            <option value="30">30 dias</option>
          </select>
          <textarea id="reason" class="swal2-textarea" placeholder="Motivo da suspensão..." rows="3"></textarea>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: "#f97316",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Suspender",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const reason = document.getElementById('reason').value;
        if (!reason) {
          Swal.showValidationMessage('Informe o motivo da suspensão');
          return false;
        }
        return {
          days: document.getElementById('days').value,
          reason: reason
        };
      }
    });

    if (formData) {
      try {
        await moderationService.suspendUser(user.id, formData.reason, formData.days);
        Swal.fire({
          icon: 'success',
          title: 'Usuário suspenso!',
          text: `Suspenso por ${formData.days} dias`,
          timer: 3000
        });
        fetchData();
      } catch (error) {
        Swal.fire('Erro!', 'Não foi possível suspender o usuário', 'error');
      }
    }
  };

  const showBanModal = async (user) => {
    const { value: reason } = await Swal.fire({
      title: 'Banir Permanentemente',
      html: `
        <div class="text-left">
          <p class="mb-2 text-sm text-gray-600">Usuário: <strong>${user.name}</strong></p>
          <p class="mb-3 text-xs text-red-600">⚠️ Esta ação é irreversível!</p>
          <textarea id="reason" class="swal2-textarea" placeholder="Motivo do banimento..." rows="3"></textarea>
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Banir Permanentemente",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const reason = document.getElementById('reason').value;
        if (!reason) {
          Swal.showValidationMessage('Informe o motivo do banimento');
          return false;
        }
        return reason;
      }
    });

    if (reason) {
      try {
        await moderationService.banUser(user.id, reason);
        Swal.fire({
          icon: 'success',
          title: 'Usuário banido!',
          text: 'O usuário foi banido permanentemente',
          timer: 3000
        });
        fetchData();
      } catch (error) {
        Swal.fire('Erro!', 'Não foi possível banir o usuário', 'error');
      }
    }
  };

  const confirmReactivate = async (user) => {
    const result = await Swal.fire({
      title: 'Reativar usuário?',
      html: `Deseja reativar o usuário <strong>${user.name}</strong>?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sim, reativar",
      cancelButtonText: "Cancelar"
    });

    if (result.isConfirmed) {
      try {
        await moderationService.reactivateUser(user.id);
        Swal.fire('Reativado!', 'Usuário reativado com sucesso', 'success');
        fetchData();
      } catch (error) {
        Swal.fire('Erro!', 'Não foi possível reativar o usuário', 'error');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#449879] mx-auto mb-4"></div>
          <p className="text-gray-500">Carregando dados de moderação...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 flex-wrap">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors flex items-center gap-2 ${
            activeTab === 'all'
              ? 'text-[#449879] border-b-2 border-[#449879]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Users className="w-4 h-4" />
          Todos os Usuários
          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
            {allUsers.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('sanctioned')}
          className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors flex items-center gap-2 ${
            activeTab === 'sanctioned'
              ? 'text-[#449879] border-b-2 border-[#449879]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Gavel className="w-4 h-4" />
          Usuários Sancionados
          {allUsers.filter(u => u.warning_count > 0 || u.suspended_until || u.is_permanently_banned).length > 0 && (
            <span className="bg-red-100 text-red-600 px-2 py-0.5 rounded-full text-xs">
              {allUsers.filter(u => u.warning_count > 0 || u.suspended_until || u.is_permanently_banned).length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('stats')}
          className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors flex items-center gap-2 ${
            activeTab === 'stats'
              ? 'text-[#449879] border-b-2 border-[#449879]'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          Estatísticas
        </button>
      </div>

      {/* Panel de usuarios */}
      {(activeTab === 'all' || activeTab === 'sanctioned') && (
        <>
          {/* Barra de búsqueda y filtros */}
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por nome, email, CPF ou telefone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#449879] focus:border-transparent"
                />
              </div>
              {activeTab === 'all' && (
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#449879]"
                >
                  <option value="all">Todos os status</option>
                  <option value="warning">Com advertências</option>
                  <option value="suspended">Suspensos</option>
                  <option value="banned">Banidos</option>
                </select>
              )}
            </div>
          </div>

          {/* Lista de usuarios */}
          <div>
            {filteredUsers.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <Shield className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-gray-500 font-medium">Nenhum usuário encontrado</p>
                <p className="text-sm text-gray-400 mt-1">Tente ajustar os filtros de busca</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredUsers.map(user => (
                  <div key={user.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-4">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <h3 className="font-semibold text-gray-800">{user.name}</h3>
                            <UserSuspensionBadge user={user} size="sm" />
                            {user.warning_count >= 3 && !user.is_permanently_banned && !user.suspended_until && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                                ⚠️ Pendente de suspensão
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mb-2">{user.email}</p>
                          <p className="text-xs text-gray-400">ID: {user.id} | CPF: {user.cpf || 'N/A'}</p>
                          
                          {user.suspension_reason && (
                            <div className="mt-2 p-2 bg-gray-50 rounded-lg text-sm">
                              <p className="text-gray-600">
                                <strong>Motivo:</strong> {user.suspension_reason}
                              </p>
                            </div>
                          )}

                          {user.infractions && user.infractions.length > 0 && (
                            <div className="mt-3">
                              <p className="text-xs text-gray-400 mb-1">Últimas infrações:</p>
                              <div className="space-y-1">
                                {user.infractions.slice(0, 3).map(inf => (
                                  <div key={inf.id} className="text-xs text-gray-600 flex items-center gap-2 flex-wrap">
                                    <span className={`w-2 h-2 rounded-full ${
                                      inf.type === 'warning' ? 'bg-yellow-500' :
                                      inf.type === 'temporary_ban' ? 'bg-orange-500' : 'bg-red-500'
                                    }`} />
                                    <span className="font-medium">{inf.type_label || inf.type}:</span>
                                    <span>{inf.reason}</span>
                                    <span className="text-gray-400">
                                      {new Date(inf.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-row md:flex-col gap-2">
                          {!user.is_permanently_banned && user.status === 'aprovado' && (
                            <>
                              <button
                                onClick={() => showWarningModal(user)}
                                className="px-3 py-1.5 bg-yellow-50 text-yellow-600 rounded-lg text-sm hover:bg-yellow-100 transition-colors flex items-center gap-1"
                                title="Aplicar advertência"
                              >
                                <AlertTriangle className="w-4 h-4" />
                                <span className="hidden sm:inline">Advertir</span>
                              </button>
                              <button
                                onClick={() => showSuspensionModal(user)}
                                className="px-3 py-1.5 bg-orange-50 text-orange-600 rounded-lg text-sm hover:bg-orange-100 transition-colors flex items-center gap-1"
                                title="Suspender temporariamente"
                              >
                                <Clock className="w-4 h-4" />
                                <span className="hidden sm:inline">Suspender</span>
                              </button>
                              <button
                                onClick={() => showBanModal(user)}
                                className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-sm hover:bg-red-100 transition-colors flex items-center gap-1"
                                title="Banir permanentemente"
                              >
                                <Ban className="w-4 h-4" />
                                <span className="hidden sm:inline">Banir</span>
                              </button>
                            </>
                          )}
                          {(user.suspended_until || user.is_permanently_banned) && (
                            <button
                              onClick={() => confirmReactivate(user)}
                              className="px-3 py-1.5 bg-green-50 text-green-600 rounded-lg text-sm hover:bg-green-100 transition-colors flex items-center gap-1"
                              title="Reativar usuário"
                            >
                              <UserCheck className="w-4 h-4" />
                              <span className="hidden sm:inline">Reativar</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Panel de estadísticas */}
      {activeTab === 'stats' && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <AlertTriangle className="w-8 h-8 text-yellow-500" />
              <span className="text-2xl font-bold text-gray-800">{stats.total_warnings || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Total de Advertências</p>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <Clock className="w-8 h-8 text-orange-500" />
              <span className="text-2xl font-bold text-gray-800">{stats.total_temporary_bans || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Suspensões Temporárias</p>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <Ban className="w-8 h-8 text-red-500" />
              <span className="text-2xl font-bold text-gray-800">{stats.total_permanent_bans || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Banimentos Permanentes</p>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <Calendar className="w-8 h-8 text-purple-500" />
              <span className="text-2xl font-bold text-gray-800">{stats.active_suspensions || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Suspensões Ativas</p>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <Gavel className="w-8 h-8 text-red-600" />
              <span className="text-2xl font-bold text-gray-800">{stats.permanently_banned || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Usuários Banidos</p>
          </div>

          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <AlertTriangle className="w-8 h-8 text-orange-500" />
              <span className="text-2xl font-bold text-gray-800">{stats.users_with_3_warnings || 0}</span>
            </div>
            <p className="text-sm text-gray-600">Com 3+ Advertências</p>
          </div>
        </div>
      )}

      {/* Botón de refresh */}
      <div className="flex justify-end">
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 transition-colors flex items-center gap-2 text-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>
    </div>
  );
}